import React, { useState } from 'react';
import { colors, typography, spacing } from '../styles/design-system';

interface VolleyballDataDisplayProps {
  data: any;
  homeTeam: string;
  awayTeam: string;
}

const VolleyballDataDisplay: React.FC<VolleyballDataDisplayProps> = ({ data, homeTeam, awayTeam }) => {
  const [selectedTeam, setSelectedTeam] = useState<'home' | 'away'>('home');

  // Debug log to check incoming data
  React.useEffect(() => {
    console.log('=== VolleyballDataDisplay Debug ===');
    console.log('Full data:', data);
    console.log('Data keys:', Object.keys(data || {}));
    console.log('Data type:', typeof data);

    console.log('\n--- head_to_head ---');
    console.log('head_to_head:', data?.head_to_head);
    console.log('head_to_head type:', typeof data?.head_to_head);
    console.log('head_to_head.records:', data?.head_to_head?.records);
    console.log('head_to_head.records length:', data?.head_to_head?.records?.length || 0);

    console.log('\n--- team_stats ---');
    console.log('team_stats:', data?.team_stats);
    console.log('team_stats type:', typeof data?.team_stats);
    console.log('team_stats keys:', Object.keys(data?.team_stats || {}));
    console.log('team_stats.three_season:', data?.team_stats?.three_season);
    console.log('team_stats.three_season length:', data?.team_stats?.three_season?.length || 0);
    console.log('team_stats.home_recent:', data?.team_stats?.home_recent);
    console.log('team_stats.away_recent:', data?.team_stats?.away_recent);

    console.log('\n--- season_records ---');
    console.log('season_records:', data?.season_records);
    console.log('season_records type:', typeof data?.season_records);
    console.log('season_records.current:', data?.season_records?.current);
    console.log('season_records.current length:', data?.season_records?.current?.length || 0);

    console.log('\n--- season_player_rankings ---');
    console.log('season_player_rankings:', data?.season_player_rankings);
    console.log('season_player_rankings type:', typeof data?.season_player_rankings);
    console.log('season_player_rankings keys:', Object.keys(data?.season_player_rankings || {}));
    console.log('season_player_rankings.home_team:', data?.season_player_rankings?.home_team);
    console.log('season_player_rankings.home_team length:', data?.season_player_rankings?.home_team?.length || 0);
    console.log('season_player_rankings.away_team:', data?.season_player_rankings?.away_team);
    console.log('season_player_rankings.away_team length:', data?.season_player_rankings?.away_team?.length || 0);

    console.log('\n--- Other fields ---');
    console.log('venue:', data?.venue);
    console.log('date_time:', data?.date_time);
    console.log('===================================');
  }, [data]);

  // Extract data from the structure
  const headToHead = data.head_to_head || {};
  const teamStats = data.team_stats || {};
  const seasonRecords = data.season_records || {};
  const seasonPlayerRankings = data.season_player_rankings || {};
  const venue = data.venue;
  const dateTime = data.date_time;

  // Render table with proper headers and data handling
  const renderTable = (tableData: any, title?: string): React.ReactElement | null => {
    if (!tableData) return null;

    let headers: string[] = [];
    let dataArray: any[][] = [];
    let description = '';

    // Handle different data formats
    if (tableData.headers && tableData.data) {
      // New format: { headers: [...], data: [[...], [...]], description: '...' }
      headers = tableData.headers;
      dataArray = tableData.data;
      description = tableData.description || '';

      // If headers is empty but data exists, use first row as headers
      if ((!headers || headers.length === 0) && dataArray.length > 0) {
        headers = dataArray[0];
        dataArray = dataArray.slice(1);
      }
    } else if (tableData.data && Array.isArray(tableData.data)) {
      // Old format: { data: [[headers], [...], [...]], description: '...' }
      const rawData = tableData.data;
      description = tableData.description || '';

      // Extract headers and data
      if (rawData.length > 0) {
        // Try to detect header row
        let headerRowIndex = 0;

        // Check if first row is title (single cell)
        if (Array.isArray(rawData[0]) && rawData[0].length === 1) {
          if (!title && !description) {
            description = rawData[0][0];
          }
          headerRowIndex = 1;
        }

        if (rawData.length > headerRowIndex) {
          headers = rawData[headerRowIndex];
          dataArray = rawData.slice(headerRowIndex + 1);
        }
      }
    } else if (Array.isArray(tableData)) {
      // Direct array format: [[headers], [...], [...]]
      if (tableData.length > 0) {
        // Skip title row if exists
        let startIdx = 0;
        if (tableData[0] && tableData[0].length === 1) {
          if (!title) {
            title = tableData[0][0];
          }
          startIdx = 1;
        }

        if (tableData.length > startIdx) {
          headers = tableData[startIdx];
          dataArray = tableData.slice(startIdx + 1);
        }
      }
    } else {
      return null;
    }

    // Validate data
    if (!headers || headers.length === 0 || dataArray.length === 0) return null;

    const tableTitle = title || description;

    return (
      <div style={styles.tableContainer}>
        {tableTitle && <h4 style={styles.tableTitle}>{tableTitle}</h4>}
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                {headers.map((header: string, idx: number) => (
                  <th key={idx} style={{
                    ...styles.th,
                    backgroundColor: idx === 0 ? '#f1f3f5' : '#f8f9fa'
                  }}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dataArray.map((row: any[], rowIdx: number) => {
                // Skip invalid rows
                if (!Array.isArray(row)) return null;

                return (
                  <tr key={rowIdx} style={{
                    backgroundColor: rowIdx % 2 === 0 ? '#ffffff' : '#fafbfc'
                  }}>
                    {headers.map((header: string, cellIdx: number) => (
                      <td key={cellIdx} style={{
                        ...styles.td,
                        fontWeight: cellIdx === 0 ? 600 : 400
                      }}>{row[cellIdx] || '-'}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Get team-specific data
  const getTeamData = () => {
    const isHome = selectedTeam === 'home';

    return {
      headToHead: (headToHead.records || []).filter((record: any) =>
        record.team_type === selectedTeam || record.team_type === null
      ),
      seasonRecords: (seasonRecords.current || []).filter((record: any) =>
        record.team_type === selectedTeam || record.team_type === null
      ),
      threeSeasonStats: (teamStats.three_season || []).filter((stat: any) =>
        stat.team_type === selectedTeam || stat.team_type === null
      ),
      recentGames: isHome ? teamStats.home_recent : teamStats.away_recent,
      // 백엔드 키: home_team, away_team (야구/농구와 통일)
      playerRankings: isHome ? seasonPlayerRankings.home_team : seasonPlayerRankings.away_team,
    };
  };

  const teamData = getTeamData();

  // Check if there's any data to display
  const hasData = (
    (headToHead && headToHead.records && headToHead.records.length > 0) ||
    (seasonRecords && seasonRecords.current && seasonRecords.current.length > 0) ||
    (teamStats && (teamStats.three_season || teamStats.home_recent || teamStats.away_recent)) ||
    (seasonPlayerRankings && (seasonPlayerRankings.home_team || seasonPlayerRankings.away_team))
  );

  if (!hasData) {
    return (
      <div style={styles.emptyState}>
        <div style={styles.emptyIcon}></div>
        <h3 style={styles.emptyTitle}>데이터가 없습니다</h3>
        <p style={styles.emptyMessage}>
          현재 이 경기에 대한 통계 데이터가 수집되지 않았음.<br />
          잠시 후 다시 시도해.
        </p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Match Info Header */}
      {(dateTime || venue) && (
        <div style={styles.matchInfoHeader}>
          <h3 style={styles.matchTitle}>
            {homeTeam} vs {awayTeam}
          </h3>
          <div style={styles.matchDetails}>
            {dateTime && <span>{dateTime}</span>}
            {venue && <span> {dateTime ? '| ' : ''}{venue}</span>}
          </div>
        </div>
      )}

      {/* Team Selection Buttons */}
      <div style={styles.teamButtons}>
        <button
          style={{
            ...styles.teamButton,
            ...(selectedTeam === 'home' ? styles.selectedTeamButton : {})
          }}
          onClick={() => setSelectedTeam('home')}
        >
          홈팀: {homeTeam}
        </button>
        <button
          style={{
            ...styles.teamButton,
            ...(selectedTeam === 'away' ? styles.selectedTeamButton : {})
          }}
          onClick={() => setSelectedTeam('away')}
        >
          원정팀: {awayTeam}
        </button>
      </div>

      {/* Team Content */}
      <div style={styles.content}>
        <h3 style={styles.teamNameTitle}>
          {selectedTeam === 'home' ? homeTeam : awayTeam} 상세 데이터
        </h3>

        {/* Head to Head Section */}
        {teamData.headToHead.length > 0 && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>상대전적</h3>
            {teamData.headToHead.map((record: any, idx: number) => (
              <div key={idx}>
                {renderTable(record)}
              </div>
            ))}
          </div>
        )}

        {/* Season Records Section */}
        {teamData.seasonRecords.length > 0 && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>시즌 전적</h3>
            {teamData.seasonRecords.map((record: any, idx: number) => (
              <div key={idx}>
                {renderTable(record)}
              </div>
            ))}
          </div>
        )}

        {/* Team Stats Section */}
        {(teamData.recentGames || teamData.threeSeasonStats.length > 0) && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>팀 통계</h3>
            {teamData.threeSeasonStats.length > 0 && (
              <>
                <h4 style={styles.subsectionTitle}>3시즌 통계</h4>
                {teamData.threeSeasonStats.map((stat: any, idx: number) => (
                  <div key={idx}>
                    {renderTable(stat)}
                  </div>
                ))}
              </>
            )}
            {teamData.recentGames && (
              <>
                <h4 style={styles.subsectionTitle}>최근 경기</h4>
                {renderTable(teamData.recentGames)}
              </>
            )}
          </div>
        )}

        {/* Season Player Rankings Section */}
        {teamData.playerRankings && teamData.playerRankings.length > 0 && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>시즌 선수 순위</h3>
            {teamData.playerRankings.map((ranking: any, idx: number) => (
              <div key={idx}>
                {renderTable(ranking)}
              </div>
            ))}
          </div>
        )}

        {/* No Data Message */}
        {teamData.headToHead.length === 0 &&
         teamData.seasonRecords.length === 0 &&
         teamData.threeSeasonStats.length === 0 &&
         !teamData.recentGames &&
         (!teamData.playerRankings || teamData.playerRankings.length === 0) && (
          <div style={styles.noData}>
            <p>{selectedTeam === 'home' ? homeTeam : awayTeam}의 상세 데이터가 없음.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxHeight: '60vh',
    padding: spacing[6],
    backgroundColor: colors.white,
    overflow: 'auto' as const,
  },
  matchInfoHeader: {
    marginBottom: spacing[7],
    paddingBottom: spacing[5],
    borderBottom: `1px solid ${colors.gray[200]}`,
  },
  matchTitle: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: typography.fontWeight.semibold,
    color: colors.black,
    marginBottom: spacing[3],
  },
  matchDetails: {
    fontSize: typography.fontSize.base,
    color: colors.gray[600],
  },
  teamButtons: {
    display: 'flex',
    marginBottom: spacing[7],
  },
  teamButton: {
    flex: 1,
    padding: spacing[4],
    backgroundColor: colors.white,
    color: colors.gray[600],
    border: `1px solid ${colors.gray[300]}`,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  selectedTeamButton: {
    backgroundColor: colors.black,
    color: colors.white,
    border: `1px solid ${colors.black}`,
  },
  content: {
    marginBottom: spacing[6],
  },
  teamNameTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.semibold,
    marginBottom: spacing[7],
    color: colors.black,
    paddingLeft: spacing[3],
    borderLeft: `3px solid ${colors.black}`,
  },
  section: {
    marginBottom: spacing[9],
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    marginBottom: spacing[6],
    color: colors.gray[800],
    borderBottom: `1px solid ${colors.gray[300]}`,
    paddingBottom: spacing[3],
  },
  subsectionTitle: {
    margin: `${spacing[6]} 0 ${spacing[4]} 0`,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium,
    color: colors.gray[700],
  },
  tableContainer: {
    marginBottom: spacing[6],
  },
  tableTitle: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing[3],
    color: colors.gray[600],
  },
  tableWrapper: {
    overflowX: 'auto' as const,
    border: `1px solid ${colors.gray[200]}`,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: typography.fontSize.base,
  },
  th: {
    padding: `${spacing[4]} ${spacing[3]}`,
    backgroundColor: colors.gray[100],
    borderBottom: `1px solid ${colors.gray[300]}`,
    borderRight: `1px solid ${colors.gray[200]}`,
    fontWeight: typography.fontWeight.semibold,
    textAlign: 'left' as const,
    whiteSpace: 'nowrap' as const,
    color: colors.gray[800],
  } as React.CSSProperties,
  td: {
    padding: spacing[3],
    borderBottom: `1px solid ${colors.gray[200]}`,
    borderRight: `1px solid ${colors.gray[200]}`,
    whiteSpace: 'nowrap' as const,
    color: colors.gray[700],
  } as React.CSSProperties,
  noData: {
    textAlign: 'center' as const,
    padding: spacing[10],
    color: colors.gray[500],
    fontSize: typography.fontSize.md,
    backgroundColor: colors.gray[50],
  } as React.CSSProperties,
  emptyState: {
    textAlign: 'center' as const,
    padding: spacing[10],
    backgroundColor: colors.gray[50],
    border: `1px solid ${colors.gray[200]}`,
  } as React.CSSProperties,
  emptyIcon: {
    fontSize: '64px',
    marginBottom: spacing[6],
    opacity: 0.5,
  } as React.CSSProperties,
  emptyTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.semibold,
    color: colors.gray[700],
    marginBottom: spacing[4],
  } as React.CSSProperties,
  emptyMessage: {
    fontSize: typography.fontSize.base,
    color: colors.gray[500],
    lineHeight: typography.lineHeight.relaxed,
  } as React.CSSProperties,
};

export default VolleyballDataDisplay;
